import { type Vec2, cellAt, cellIndex } from '@d2/core';
import { type BodyAction } from '@d2/schema';

import { type DrawnFloor } from './cave';
import { type PendingIntent } from './prediction';
import { type Pursuit, askedAgain, beginPursuit, pursue } from './pursuit';
export type { EntityPositions } from './session';
import {
  type EntityPositions,
  type FloorArrival,
  type GroundItemSnapshot,
  type MonsterSnapshot,
  type Session,
  type WorldSnapshot,
  actEntity,
  advanceSession,
  enterFloor,
  forgetGroundItem,
  forgetMonster,
  forgetRemote,
  nearestTargetId,
  orderMoveTo,
  placeGroundItem,
  placeLocal,
  placeMonster,
  placeRemote,
  positionsAt,
  receiveSnapshot,
  takeSequence,
} from './session';

/**
 * The one thing the renderer is allowed to know about the world.
 *
 * `DungeonScene` draws sprites and forwards clicks; it holds no position, no
 * route and no clock, and it must not learn what a socket is. So it is given
 * this: a name for the entity it drives, somewhere to send a click, and a
 * question it asks once a frame. Everything behind it — prediction,
 * interpolation, the clock estimate, the room — is `./session`'s and
 * `./room-link`'s business.
 *
 * The port exists because there are two honest ways to answer those questions
 * and the renderer should not be able to tell them apart. In a room, positions
 * come from the server and a click becomes an intent on the wire. Without one,
 * the same `Session` runs with no snapshots ever arriving — which is exactly
 * what a client with nothing to talk to *is*, rather than a second
 * implementation of anything: the prediction, the routing and the fixed step
 * are the same code either way, and the only difference is that nobody
 * corrects it.
 *
 * ## Why this one is mutable
 *
 * `./session` is a fold and every function in it returns a new value, which is
 * what makes a jittery minute of network replayable in a unit test. Something
 * still has to hold the newest one and hand it to the next frame, and a Phaser
 * scene calling `update` sixty times a second is not going to thread a value
 * through itself. This is the smallest possible box around that: one field, one
 * assignment per call, no logic. Anything that looks like a decision belongs on
 * the other side of it.
 */
export interface WorldView {
  /** The entity this client drives — the one that is predicted rather than interpolated. */
  readonly localId: string;
  /**
   * The floor being drawn: the verified cave, and the cell its stairs are on.
   *
   * A **value that is replaced**, not mutated — a descent hands back a different
   * object, and the renderer compares by identity to decide whether the ground
   * under it is still the ground it laid out. Identity rather than a depth or a
   * revision number because it cannot be wrong: one floor is one object, and the
   * only way to get a new one is `arrive`.
   *
   * It is on the renderer's own port rather than passed to the scene once,
   * because a scene constructed with a cave holds the cave it was constructed
   * with — and `scene.restart()` does not run a constructor.
   */
  readonly floor: DrawnFloor;
  /**
   * Spends time on the local player's prediction. Once per frame, before
   * drawing.
   *
   * Two readings, because they answer different questions: `localTimeMs` is
   * where the browser's clock is, which is what the server's tick is derived
   * from; `frameMs` is how long this frame was, which is all there is to go on
   * when no server has been heard from.
   */
  readonly advance: (localTimeMs: number, frameMs: number) => void;
  /** Where to draw everybody at one reading of the browser's clock. */
  readonly positionsAt: (localTimeMs: number) => EntityPositions;
  /** "Walk to this cell" — predicted at once, and sent if there is anybody to send it to. */
  readonly moveTo: (cell: Vec2) => void;
  /**
   * "Hit that body" — the intent, and nothing about the outcome.
   *
   * Predicts **nothing**: whether the swing may happen at all is `admitSwing`'s,
   * on the server, and what it takes off is a damage number `ARCHITECTURE.md`
   * forbids predicting. What this does own is the *pursuit* — a swing the server
   * refuses is followed by a walk toward the body and asked again, which is D2's
   * behaviour and is `./pursuit`'s decision rather than this one's.
   *
   * The clock reading is the frame's own, the same one `positionsAt` is asked
   * with, so the body a pursuit walks toward is the body that was on screen when
   * the player clicked it.
   */
  readonly attack: (targetId: string, localTimeMs: number) => void;
  /**
   * "Take that thing off the floor" — the intent, and nothing about the outcome.
   *
   * Whether the drop still exists, whether somebody else took it a tick ago,
   * whether this character can reach it and where it lands in the grid are all
   * the room's, and loot binds to a character server-side at pickup
   * (`ARCHITECTURE.md`). Nothing is removed from the floor here: the drop
   * disappears when the room stops replicating it, which is the same channel a
   * *party member's* pickup arrives on.
   */
  readonly pickUp: (itemId: string) => void;
  /**
   * "I am taking the stairs" — the intent, and nothing about the outcome.
   *
   * Predicts **nothing**, and there is a great deal here not to predict: the
   * seed and the depth of the floor below, the population rolled onto it, and
   * where every seat lands are all the room's (`@d2/schema`'s `DescendPayload`
   * carries a sequence and not one word else). The client finds out it worked
   * because the floor changes underneath it.
   *
   * Like a swing it owns the *pursuit*: the room takes a descent only from a
   * body close enough to touch the stairs — the very reach a pickup is judged by
   * — so a refusal is followed by a walk onto the cell and asked again, which is
   * `./pursuit`'s decision and not this one's.
   *
   * The clock reading is the frame's own, the same one `positionsAt` is asked
   * with, exactly as `attack`'s is.
   */
  readonly descend: (localTimeMs: number) => void;
  /** Renderer sandbox: stand the local player somewhere. A room overwrites it. */
  readonly placeLocal: (position: Vec2) => void;
  /** Renderer sandbox: stand another entity somewhere. A room overwrites it. */
  readonly placeRemote: (id: string, position: Vec2) => void;
  /**
   * Renderer sandbox: say what a player — this client's own body included — is
   * doing. A room owns that and the next snapshot overwrites it.
   *
   * On the port the scene is handed rather than on `ConnectedWorldView`, unlike
   * the two questions a panel asks, because the *browser suite* drives the scene
   * and three of the seven states cannot be produced by a room at all: blocking
   * has no rule behind it yet, casting is invisible for eight of the nine
   * skills, and a corpse costs a character (see `session.ts`'s `actEntity`).
   */
  readonly actEntity: (id: string, action: BodyAction) => void;
  /** Renderer sandbox: take another entity off the map. */
  readonly forgetRemote: (id: string) => void;
  /** Renderer sandbox: stand a monster somewhere, described. A room owns its own population. */
  readonly placeMonster: (monster: MonsterSnapshot) => void;
  /** Renderer sandbox: take a monster off the floor — what a death looks like from the renderer. */
  readonly forgetMonster: (id: string) => void;
  /** Renderer sandbox: lay a drop on the floor. A room owns what is lying on its own. */
  readonly placeGroundItem: (item: GroundItemSnapshot) => void;
  /** Renderer sandbox: take a drop off the floor — what a pickup looks like from the renderer. */
  readonly forgetGroundItem: (id: string) => void;
}

/**
 * A view that can also be fed by a room, and asked the two questions a panel has
 * that a scene does not.
 *
 * The extra members are deliberately **not** on `WorldView`. That port is what a
 * Phaser scene is handed and it is meant to stay the smallest thing that can
 * draw a frame; the overlay is a different consumer with a different need — it
 * puts intents on the wire that no click produced — and widening the renderer's
 * port to serve it would tell the scene about casting.
 */
export interface ConnectedWorldView extends WorldView {
  /** Folds one authoritative snapshot in, stamped with the browser time it landed at. */
  readonly receive: (snapshot: WorldSnapshot, localTimeMs: number) => void;
  /**
   * The party has descended: here is the new floor, and the body that walks it.
   *
   * The one operation that replaces the world under a live view. What crosses
   * and what does not is `session.ts`'s `enterFloor`; what this adds is the two
   * things that live out here — the floor the renderer reads, and the pursuit,
   * which is dropped because whatever it was chasing is a floor above.
   *
   * It is on `ConnectedWorldView` and not on the renderer's port for the reason
   * `receive` is: only a room can produce it, and a scene that could change its
   * own floor would be a renderer with a rule in it.
   */
  readonly arrive: (floor: DrawnFloor, arrival: FloorArrival) => void;
  /** The session as it stands. Read-only — for tests and diagnostics, never for a rule. */
  readonly session: Session;
  /**
   * Takes the next intent sequence, spending it.
   *
   * A cast and a skill-point spend are intents like a click and are
   * acknowledged through the same single field, so they draw from the same
   * counter — see `session.ts`'s `takeSequence`.
   */
  readonly nextSequence: () => number;
  /**
   * The body a hotkey aims at, at one reading of the browser's clock, or `null`
   * when nothing is being drawn to aim at.
   *
   * A targeting affordance, not a rule: whether the cast may land is
   * `admitCast`'s, on the server.
   */
  readonly nearestTarget: (localTimeMs: number) => string | null;
}

/**
 * Where the four intents a **click** can produce go: a room's, or nowhere.
 *
 * One object rather than four parameters, because they are one thing — the
 * outward edge of this box — and because a caller that wired up three of them
 * and forgot the fourth would have a game where loot could not be picked up, or
 * where the stairs did nothing, and nothing would say so.
 *
 * A walk carries the whole `PendingIntent` because the prediction produced it
 * and holds it until the server answers; a swing and a pickup carry an id and a
 * sequence, because there is nothing else to them and nothing to predict. A
 * descent carries **only** the sequence, because that is the whole of the
 * message: where the stairs are is the floor's, and which floor is below this
 * one is core's (`@d2/schema`'s `DescendPayload`).
 */
export interface IntentSinks {
  readonly moveTo: (intent: PendingIntent) => void;
  readonly attack: (targetId: string, sequence: number) => void;
  readonly pickUp: (itemId: string, sequence: number) => void;
  readonly descend: (sequence: number) => void;
}

/** Sends nowhere. A client with no room still predicts its own clicks; nobody hears about them. */
const unsent: IntentSinks = {
  moveTo: (): void => undefined,
  attack: (): void => undefined,
  pickUp: (): void => undefined,
  descend: (): void => undefined,
};

/**
 * What a pursuit is chasing — the half `./pursuit` deliberately does not hold.
 *
 * The same two shapes `targeting.ts` answers a click with, minus the drop: a
 * pickup is resolved where it arrives and has nothing to walk toward. It lives
 * here rather than in `./pursuit` because it is the *caller's* half of the
 * chase: what to walk to, whether it is still there, and how to ask again.
 */
type Quarry = { readonly kind: 'monster'; readonly id: string } | { readonly kind: 'stairs' };

/** One unanswered ask, and what it was aimed at. */
interface Chase {
  readonly quarry: Quarry;
  readonly pursuit: Pursuit;
}

/**
 * Wraps a session so a scene can drive it.
 *
 * The intent sinks are the only outward edge: `moveTo` predicts first and sends
 * second, in that order and never the other way round, because the walk
 * starting on this frame is the entire point of predicting and a send that
 * threw would otherwise swallow the click.
 *
 * ## The one pursuit
 *
 * An ask the server refuses is followed up — walk toward the thing, ask again —
 * and something has to hold that between frames, exactly as something has to
 * hold the session. It is **one** pursuit and not a queue, because the newest
 * thing the player asked for is the thing they want: every other order clears
 * it, and a second click on a second monster replaces it. What that pursuit
 * *does* is `./pursuit`'s, which is pure and has no idea what a socket is.
 *
 * **Two intents go through it, not one.** A swing and a descent are the same
 * shape from here: both are refused silently by a server that judges reach with
 * `canTouch`, and the answer to both is to close the distance and ask again. So
 * the retry machine is shared and only the *quarry* differs — which body to walk
 * to or which cell, whether the thing is still there, and which sink to send to.
 *
 * ## The floor is held here too, and replaced whole
 *
 * A descent rebuilds the world under a live view. The session's half of that is
 * `enterFloor`; this holds the other half — the `DrawnFloor` the renderer reads
 * — and replaces the object rather than editing it, so a scene comparing by
 * identity can tell that the ground moved.
 */
export const createWorldView = (
  initial: Session,
  initialFloor: DrawnFloor,
  send: IntentSinks = unsent,
): ConnectedWorldView => {
  let current = initial;
  /** The floor being drawn. Replaced whole by `arrive`, never edited. */
  let floor = initialFloor;
  /** The click that is still waiting for the server to act on it, and what it was aimed at. */
  let chase: Chase | null = null;

  /** Takes the next intent sequence off the one shared counter, spending it. */
  const nextSequence = (): number => {
    const taken = takeSequence(current);

    current = taken.session;

    return taken.sequence;
  };

  /**
   * Orders a walk: predicted first, sent second.
   *
   * Shared by the player's own click and by a pursuit closing the distance, so
   * the two cannot walk by different rules. What is *not* shared is clearing the
   * pursuit — a pursuit ordering a walk must not cancel itself.
   */
  const walk = (cell: Vec2): void => {
    const ordered = orderMoveTo(current, cell);

    current = ordered.session;
    // A click off the map walks nothing and is not worth an intent — see
    // `orderMoveTo`. Everything else goes on the wire, routable or not.
    if (ordered.intent !== null) send.moveTo(ordered.intent);
  };

  /** Asks once for whatever this quarry is worth, and hands back the sequence it went out under. */
  const ask = (quarry: Quarry): number => {
    const sequence = nextSequence();

    if (quarry.kind === 'monster') {
      send.attack(quarry.id, sequence);
    } else {
      send.descend(sequence);
    }

    return sequence;
  };

  /**
   * Whether the thing being chased is still there to chase.
   *
   * A body is there while the room is still replicating it — a corpse is not
   * worth asking about. A floor's way down cannot go anywhere, so a descent ends
   * only on the acknowledgement or the deadline.
   */
  const stillThere = (quarry: Quarry): boolean =>
    quarry.kind === 'stairs' || current.monsters.has(quarry.id);

  /**
   * The cell to walk to when the server refuses, or `null` when there is not
   * one this floor has.
   *
   * Walk *onto* the thing's own cell in both cases. The server resolves that
   * with `nearestReachable` and stops the step at contact, which is the closest
   * this client can ask to stand without restating what "close enough" means —
   * and for the stairs it is exactly right, since standing on them is what the
   * room is measuring.
   */
  const approachCell = (quarry: Quarry, localTimeMs: number): Vec2 | null => {
    const grid = current.mover.clearance.grid;

    if (quarry.kind === 'stairs') return floor.stairs;

    const body = positionsAt(current, localTimeMs).monsters.get(quarry.id);
    const index = body === undefined ? -1 : cellIndex(grid, body.position);

    return index >= 0 ? cellAt(grid, index) : null;
  };

  /**
   * One frame of a pursuit.
   *
   * The walk goes out **before** the ask, which is what makes the
   * acknowledgement mean one thing: the move carries the lower sequence, so
   * `acknowledged >= pursuit.sequence` can only be the ask (`./pursuit`).
   */
  const pursueFrame = (localTimeMs: number): void => {
    const active = chase;
    if (active === null) return;

    const step = pursue(active.pursuit, {
      acknowledged: current.prediction.acknowledged,
      present: stillThere(active.quarry),
      nowMs: localTimeMs,
    });

    if (step === 'wait') return;

    if (step === 'done') {
      chase = null;
      return;
    }

    if (step === 'approach') {
      const cell = approachCell(active.quarry, localTimeMs);

      if (cell !== null) walk(cell);
    }

    chase = { quarry: active.quarry, pursuit: askedAgain(active.pursuit, ask(active.quarry), localTimeMs) };
  };

  /** Begins a pursuit: the first ask has gone out, and this is what is left to do about it. */
  const begin = (quarry: Quarry, localTimeMs: number): void => {
    // The ask goes out before the pursuit exists, so the pursuit is built around
    // the sequence that actually left — and a second click replaces the first
    // rather than queueing behind it.
    chase = { quarry, pursuit: beginPursuit(ask(quarry), localTimeMs) };
  };

  return {
    localId: initial.localId,

    get session(): Session {
      return current;
    },

    get floor(): DrawnFloor {
      return floor;
    },

    advance: (localTimeMs: number, frameMs: number): void => {
      current = advanceSession(current, localTimeMs, frameMs);
      pursueFrame(localTimeMs);
    },

    positionsAt: (localTimeMs: number): EntityPositions => positionsAt(current, localTimeMs),

    nextSequence,

    nearestTarget: (localTimeMs: number): string | null => nearestTargetId(positionsAt(current, localTimeMs)),

    moveTo: (cell: Vec2): void => {
      // A walk is a change of mind about what to do next, so whatever was being
      // chased is no longer being chased.
      chase = null;
      walk(cell);
    },

    attack: (targetId: string, localTimeMs: number): void => {
      begin({ kind: 'monster', id: targetId }, localTimeMs);
    },

    pickUp: (itemId: string): void => {
      chase = null;
      send.pickUp(itemId, nextSequence());
    },

    descend: (localTimeMs: number): void => {
      // A floor with no way down cannot be asked to be left. That is the
      // renderer sandbox and nothing else: a room's floor is refused outright
      // unless its stairs verify (`./cave`).
      if (floor.stairs === null) return;

      begin({ kind: 'stairs' }, localTimeMs);
    },

    receive: (snapshot: WorldSnapshot, localTimeMs: number): void => {
      current = receiveSnapshot(current, snapshot, localTimeMs);
    },

    arrive: (next: DrawnFloor, arrival: FloorArrival): void => {
      floor = next;
      current = enterFloor(current, arrival);
      // Whatever was being chased is on the floor above: a descent that was
      // acknowledged is finished, and a body is one this room has stopped
      // replicating under an id the new floor is about to reuse.
      chase = null;
    },

    placeLocal: (position: Vec2): void => {
      current = placeLocal(current, position);
    },

    placeRemote: (id: string, position: Vec2): void => {
      current = placeRemote(current, id, position);
    },

    actEntity: (id: string, action: BodyAction): void => {
      current = actEntity(current, id, action);
    },

    forgetRemote: (id: string): void => {
      current = forgetRemote(current, id);
    },

    placeMonster: (monster: MonsterSnapshot): void => {
      current = placeMonster(current, monster);
    },

    forgetMonster: (id: string): void => {
      current = forgetMonster(current, id);
    },

    placeGroundItem: (item: GroundItemSnapshot): void => {
      current = placeGroundItem(current, item);
    },

    forgetGroundItem: (id: string): void => {
      current = forgetGroundItem(current, id);
    },
  };
};
